import { fromUnixTime } from 'date-fns';
import cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import {
    BehaviorSubject,
    catchError,
    concat,
    finalize,
    ignoreElements,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    switchMapTo,
    takeWhile,
    tap,
    throwError,
} from 'rxjs';
import {
    cookieKeyRefreshToken,
    storageKeyPassword,
    storageKeyToken,
    storageKeyUsername,
} from 'config/auth';
import {
    CredentialsReadError,
    RefreshTokenReadError,
    TokenExpiredError,
    TokenReadError,
    UnauthorizedError,
} from 'lib/errors';
import { get$, post$ } from 'services/axios';
import logger from 'services/logger';
import { AccessToken, AuthorizationResponse, AuthorizationSigninPayload } from 'types/dto';

/**
 * Check if running inside an iframe
 */
const isIframeMode = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

/**
 * Get refresh token from URL parameters
 */
const getUrlRefreshToken$ = () =>
    new Observable<string>(subscriber => {
        if (!isIframeMode()) {
            return subscriber.error(new RefreshTokenReadError('Not in iframe mode'));
        }

        const urlParams = new URLSearchParams(window.location.search);
        const refreshToken = urlParams.get('refreshToken');
        console.log("URL refreshTOken from auth:", refreshToken);
        if (!refreshToken) {
            return subscriber.error(new RefreshTokenReadError('Refresh token not found in URL'));
        }

        logger.debug('auth', 'Using refresh token from URL');
        sessionStorage.setItem('isIframeMode', 'true');
        subscriber.next(refreshToken);
    });

/**
 * Persist specified token response to session storage
 */
export const storeTokenResponse$ = (response: AuthorizationResponse) =>
    of(response).pipe(
        tap(() => logger.debug('auth', 'Persisting authorization response')),
        tap(value => {
            const json = JSON.stringify(value);
            sessionStorage.setItem(storageKeyToken, json);
        }),
    );

/**
 * Handle token requests by wrapping them in status notifiers and persisting response payloads to
 * session storage upon successful completion
 *
 * @param {Subject<boolean>} requesting$ Subject accepting updates to the current token status
 * @param {Observable<AuthorizationResponse>} request$ Target API request observable
 */
const getAuthorizationResponse$ = (
    requesting$: Subject<boolean>,
    request$: Observable<AuthorizationResponse>,
) =>
    of(null).pipe(
        tap(() => requesting$.next(true)),
        switchMapTo(
            request$.pipe(
                switchMap(storeTokenResponse$),
                tap(() => requesting$.next(false)),
            ),
        ),
        finalize(() => requesting$.next(false)),
    );

/**
 * Create an observable emitting refresh tokens found in the cookie key set by SmartPay
 */
const getCookieRefreshToken$ = () =>
    new Observable<string>(subscriber => {
        try {
            const refreshToken = cookies.get(cookieKeyRefreshToken) ?? null;
            console.log('refreshToken From Smartpay', refreshToken);
            if (!refreshToken) {
                return subscriber.error(
                    new RefreshTokenReadError('Refresh token not found in the application cookie'),
                );
            }

            logger.debug('auth', 'Using refresh token from the application cookie');
            subscriber.next(refreshToken);
        } catch (e) {
            return subscriber.error(
                new RefreshTokenReadError('Error reading refresh token from the application cookie'),
            );
        }
    });

/**
 * Create an observable emitting credentials found in browser's local storage
 */
const getStorageCredentials$ = () =>
    new Observable<[string, string]>(subscriber => {
        let username: Nullable<string>;
        let password: Nullable<string>;

        try {
            username = localStorage.getItem(storageKeyUsername) ?? null;
            password = localStorage.getItem(storageKeyPassword) ?? null;
        } catch (e) {
            return subscriber.error(
                new CredentialsReadError('Failed reading local authentication credentials'),
            );
        }

        if (!username || !password) {
            return subscriber.error(
                new CredentialsReadError('Local authentication credentials not found'),
            );
        }

        logger.debug('auth', 'Using user credentials from local storage');
        subscriber.next([username, password]);
    });

/**
 * Create an observable emitting the token available in local storage
 */
const getLocalToken$ = () =>
    new Observable<AuthorizationResponse>(subscriber => {
        const json = sessionStorage.getItem(storageKeyToken);
        if (!json) {
            return subscriber.error(new TokenReadError('Token data not found in session storage'));
        }

        try {
            const payload: AuthorizationResponse = JSON.parse(json);
            const token = jwtDecode<AccessToken>(payload.accessToken);
            const now = new Date();
            const expiryTime = fromUnixTime(token.exp);

            if (expiryTime < now) {
                return subscriber.error(new TokenExpiredError(token, payload.refreshToken));
            }

            logger.debug('auth', 'Using token from the session storage');
            subscriber.next(payload);
        } catch (e) {
            return subscriber.error(new TokenReadError('Failed parsing stored token data'));
        }
    });

/**
 * Create an observable emitting a token received from the server in exchange for a refresh token
 *
 * @param {string} refreshToken Refresh token to exchange
 */
export const getRefreshToken$ = (refreshToken: string) =>
    post$<AuthorizationResponse>('/v1/account/refresh', { refreshToken }).pipe(
        tap(() => logger.debug('auth', 'Token successfully refreshed')),
        catchError((error: Error) =>
            throwError(
                () => new UnauthorizedError(`Error refreshing token (${error.message.toLowerCase()})`),
            ),
        ),
    );

/**
 * Create an observable emitting a token retrieved from the server in exchange for credentials
 *
 * @param {string} username Username
 * @param {string} password Password
 */
export const getSigninToken$ = (username: string, password: string) =>
    post$<AuthorizationResponse, AuthorizationSigninPayload>('/v1/account/signin', {
        userName: username,
        password,
    });

export const getSystemName$ = (): Observable<string> =>
    new Observable((observer) => {
        observer.next(null); // Initial trigger
        observer.complete();
    }).pipe(
        switchMap(() => get$<string>('/v1/system-name'))
    );

   export const getBankRecSettings$ = (settingsType: string): Observable<string> =>
    new Observable((observer) => {
        console.log('calling get bankrec settings with', settingsType);
        observer.next(null);
        observer.complete();
    }).pipe(
        switchMap(() => get$<string>(`/v1/bank-rec-settings?settingstype=${settingsType}`))
    );


export const getIsAccessThroughSP6$ = (): Observable<string> =>
    new Observable((observer) => {
        console.log('calling get IsAccessThroughSP6');
        observer.next(null);
        observer.complete();
    }).pipe(
        switchMap(() => get$<string>('/v1/get-access-sp6'))
    );

/**
 * Create an observable emitting a token received from the server in exchange for a refresh token
 * found in the local cookie that's set by SmartPay. If the refresh token cookie does not exist
 * (e.g. in case of running the application standalone) use credentials stored in browser's local
 * storage. If no credentials are found fail with an exception.
 */
const getServerToken$ = () =>
    getUrlRefreshToken$().pipe(
        switchMap(getRefreshToken$),
        catchError((error: Error) => {
            // If URL token fails or doesn't exist, fall back to cookie token
            if (error instanceof RefreshTokenReadError) {
                logger.debug('auth', error.message);
                return getCookieRefreshToken$().pipe(
                    switchMap(getRefreshToken$),
                    catchError((error: Error) => {
                        // If cookie token fails, fall back to local storage credentials
                        if (error instanceof RefreshTokenReadError) {
                // Log errors associated with reading refresh token cookies
                            logger.debug('auth', error.message);

                // Retrieve token from the server in exchange for the local storage credentials
                            return getStorageCredentials$().pipe(
                                switchMap(([username, password]) =>
                                    getSigninToken$(username, password).pipe(
                                        tap(() => logger.debug('auth', 'Token successfully retrieved')),
                                        catchError((error: Error) =>
                                            throwError(
                                                () => new UnauthorizedError(`Error requesting token (${error.message})`),
                                            ),
                                        ),
                                    ),
                                ),
                                catchError((error: Error) => {
                        // Failed reading credentials from the local storage
                                    if (error instanceof CredentialsReadError) {
                            // Log errors associated with reading local storage credentials
                                        logger.debug('auth', error.message);
                                        return throwError(
                                            () => new UnauthorizedError('No valid authentication methods found on this device'),
                                        );
                                    }
                                    return throwError(() => error);
                                }),
                            );
                        }
                        return throwError(() => error);
                    }),
                );
            }
            return throwError(() => error);
        }),
    );

/**
 * Retrieve the latest valid token response from either browser's session storage or server if
 * nothing was found in said storage.
 *
 * @param {Subject<boolean>} requesting$ Subject accepting updates to the current token status
 */
const getToken$ = (requesting$: Subject<boolean>) =>
    getLocalToken$().pipe(
        catchError((error: Error) => {
            logger.debug('auth', error.message);

            if (error instanceof TokenReadError) {
                return getAuthorizationResponse$(requesting$, getServerToken$());
            }

            if (error instanceof TokenExpiredError) {
                const refreshToken = error.getRefreshToken();
                return getAuthorizationResponse$(requesting$, getRefreshToken$(refreshToken));
            }

            return throwError(() => new UnauthorizedError('Authentication failed'));
        }),
    );

/**
 * Create a semaphore observable that only completes when no requests are in flight
 *
 * @param {Subject<boolean>} requesting$ Source request status observable
 */
const createTokenRequestSemaphore$ = (requesting$: Subject<boolean>) =>
    requesting$.pipe(
        takeWhile(requesting => requesting),
        ignoreElements(),
    );

/**
 * Create observables allowing to update and monitor current token request status
 */
export const createTokenRequestStatus$ = () => new BehaviorSubject<boolean>(false);

/**
 * Create an observable emitting the latest available access token
 *
 * @param {Subject<boolean>} requesting$ Source request status observable
 */
export const createToken$ = (requesting$: Subject<boolean>) =>
    concat(createTokenRequestSemaphore$(requesting$), getToken$(requesting$)).pipe(
        map(token => token.accessToken),
    );